// src/components/common/vibe/GifPicker.js
import React, { useState, useEffect } from 'react';
import { Film, Search, TrendingUp, X } from 'lucide-react';

// Free public Giphy API key (rate-limited but suitable for development)
// For production, replace with your own API key from https://developers.giphy.com/
const GIPHY_API_KEY = 'sXpGFDGZs0Dv1mmNFvYaGUvYwKX0PWIh';
const GIPHY_API_BASE = 'https://api.giphy.com/v1/gifs';

const GifPicker = ({ onGifSelect, theme }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [gifs, setGifs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedGif, setSelectedGif] = useState(null);

    // Load trending GIFs on open
    useEffect(() => {
        if (isOpen && gifs.length === 0) {
            loadTrendingGifs();
        }
    }, [isOpen]);

    const loadTrendingGifs = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${GIPHY_API_BASE}/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=g`
            );
            const data = await response.json();
            setGifs(data.data || []);
        } catch (error) {
            console.error('Error loading trending GIFs:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchGifs = async (query) => {
        if (!query.trim()) {
            loadTrendingGifs();
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(
                `${GIPHY_API_BASE}/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query)}&limit=20&rating=g`
            );
            const data = await response.json();
            setGifs(data.data || []);
        } catch (error) {
            console.error('Error searching GIFs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Debounce search
        if (window.gifSearchTimeout) {
            clearTimeout(window.gifSearchTimeout);
        }

        window.gifSearchTimeout = setTimeout(() => {
            searchGifs(value);
        }, 500);
    };

    const handleGifClick = (gif) => {
        setSelectedGif(gif);
    };

    const handleGifConfirm = () => {
        if (selectedGif) {
            onGifSelect({
                url: selectedGif.images.fixed_height.url,
                title: selectedGif.title,
                width: selectedGif.images.fixed_height.width,
                height: selectedGif.images.fixed_height.height
            });
            setIsOpen(false);
            setSelectedGif(null);
            setSearchTerm('');
        }
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`p-2 rounded-lg transition-colors ${
                    theme === 'dark'
                        ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                title="Add GIF"
            >
                <Film className="w-5 h-5" />
            </button>

            {isOpen && (
                <>
                    {/* Backdrop to close picker */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => {
                            setIsOpen(false);
                            setSelectedGif(null);
                        }}
                    />

                    {/* GIF Picker Popup */}
                    <div className={`absolute top-full mt-2 left-0 z-50 w-96 rounded-lg shadow-xl border ${
                        theme === 'dark'
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                    }`}>
                        {/* Header */}
                        <div className={`p-3 border-b flex items-center justify-between ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <div className="flex items-center gap-2">
                                <Film className="w-5 h-5 text-blue-600" />
                                <h3 className={`font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Choose a GIF
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsOpen(false);
                                    setSelectedGif(null);
                                }}
                                className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Search */}
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="relative">
                                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    placeholder="Search GIFs..."
                                    className={`w-full pl-9 pr-3 py-2 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                />
                            </div>
                            {!searchTerm && (
                                <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                    <TrendingUp className="w-3 h-3" />
                                    <span>Showing trending GIFs</span>
                                </div>
                            )}
                        </div>

                        {/* Selected GIF Preview */}
                        {selectedGif && (
                            <div className={`p-3 border-b ${
                                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                            }`}>
                                <div className="flex items-start gap-3">
                                    <img
                                        src={selectedGif.images.fixed_height_small.url}
                                        alt={selectedGif.title}
                                        className="w-20 h-20 rounded object-cover"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {selectedGif.title || 'Untitled GIF'}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleGifConfirm}
                                            className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors w-full"
                                        >
                                            Insert GIF
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* GIF Grid */}
                        <div className="p-3 max-h-96 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : gifs.length === 0 ? (
                                <div className={`text-center py-12 ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                    <Film className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">No GIFs found</p>
                                    {searchTerm && (
                                        <p className="text-xs mt-1">Try a different search term</p>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    {gifs.map((gif) => (
                                        <button
                                            key={gif.id}
                                            type="button"
                                            onClick={() => handleGifClick(gif)}
                                            className={`relative rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all ${
                                                selectedGif?.id === gif.id
                                                    ? 'ring-2 ring-blue-600'
                                                    : ''
                                            }`}
                                        >
                                            <img
                                                src={gif.images.fixed_height_small.url}
                                                alt={gif.title}
                                                className="w-full h-32 object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className={`p-2 border-t text-center ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <a
                                href="https://giphy.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                } hover:underline`}
                            >
                                Powered by GIPHY
                            </a>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default GifPicker;
